/**
 * Audit Log API Service
 */

import { apiRequest } from "../../api/apiClient";
import type { AuditLog } from "../../types/audit.types";

const handleResponse = async (response: Response) => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "API request failed");
  }

  // Check if response has content before parsing JSON
  const contentType = response.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) {
    return response.json();
  }

  // For 204 No Content or other responses without JSON body
  return null;
};

export const auditService = {
  getAuditLogs: async (): Promise<AuditLog[]> => {
    const response = await apiRequest("/AuditLogs", {
      method: "GET",
    });
    return handleResponse(response);
  },

  getHistory: async (
    entityName: string,
    recordId: number,
  ): Promise<AuditLog[]> => {
    const response = await apiRequest(
      `/AuditLogs/history?entityName=${encodeURIComponent(entityName)}&recordId=${recordId}`,
      {
        method: "GET",
      },
    );

    const payload = await handleResponse(response);
    if (Array.isArray(payload)) {
      return payload as AuditLog[];
    }

    if (payload && Array.isArray((payload as any).data)) {
      return (payload as any).data as AuditLog[];
    }

    if (payload && Array.isArray((payload as any).items)) {
      return (payload as any).items as AuditLog[];
    }

    return [];
  },

  getAuditLogById: async (id: number): Promise<AuditLog> => {
    const response = await apiRequest(`/AuditLogs/${id}`, {
      method: "GET",
    });
    return handleResponse(response);
  },
};
